import { createPopup } from "@typeform/embed";
import "@typeform/embed/build/css/popup.css";
import { IWebflowBenefitCategory } from "@wingspanhq/cms/dist/lib/interfaces/webflow/benefits";
import {
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { INewUser } from "@wingspanhq/users/dist/lib/interfaces";
import React, { useCallback } from "react";
import { RouteComponentProps } from "react-router";
import { Redirect, Route, Switch } from "react-router-dom";
import { useBrowserPageTitle } from "../../../../components/BrowserPageTitle/BrowserPageTitle";
import { Tabs } from "../../../../components/Tabs";
import { WSQueries } from "../../../../query/WSQuery";
import { useCMSWebflowBenefits } from "../../../../query/cms/queries";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { getCurrentEntepriseId } from "../../../../query/platform/selectors";
import { useSubscriptionV3Query } from "../../../../query/subscriptions/queries";
import { useActivities, useUserProfile } from "../../../../query/users/queries";
import { useWSStore } from "../../../../store";
import { useShowMasterPolicyFlow } from "../../../../userFeatures/hooks/useShowMasterPolicyFlow";
import {
  getSegmentAnonymousIdFromLocalStorage,
  track
} from "../../../../utils/analytics";
import { BenefitsRequestBenefit } from "../../modals/BenefitsRequestBenefit";
import {
  getBenefitsInsuranceDetailsPathname,
  PATH_BENEFITS_INSURANCE
} from "../../paths";
import { selectorBenefitsList } from "../../selectors/selectorBenefitsList";
import { ExtendedWebflowBenefit } from "../../types";
import styles from "../Benefits.module.scss";
import { BenefitsCMSBlock } from "../BenefitsCMSBlock";
import { BenefitsInsuranceBlock } from "../BenefitsInsuranceBlock";
import { BenefitsInsuranceTab } from "./contsants";

export const RouteBenefitsInsurance: React.FC<RouteComponentProps> = () => {
  useBrowserPageTitle("Benefits - Insurance");
  const userId = useUserId();
  const userProfileQuery = useUserProfile(userId);
  const qFeatureFlags = useFeatureFlags();
  const store = useWSStore();
  const qActivities = useActivities(userId);
  const qCMSWebflowBenefits = useCMSWebflowBenefits();
  const qSubscription = useSubscriptionV3Query(userId);
  const showMasterPolicyEnrollment = useShowMasterPolicyFlow();

  const openRequestBenefitModal = useCallback(() => {
    const anonymousId = getSegmentAnonymousIdFromLocalStorage();
    const {
      email,
      profile: { firstName, lastName }
    } = userProfileQuery.data as INewUser;
    const fullName = `${firstName} ${lastName}`;
    const typeFormReference = createPopup(
      `https://wingspan.typeform.com/to/xXS6mQu8#name=${fullName}&email=${email}&userid=${userId}&anonymousid=${anonymousId}`,
      {
        onSubmit: responseId => {
          track("Request a new benefit form submitted", {
            responseId: responseId
          });
        }
      }
    );
    typeFormReference.open();
  }, [userId, userProfileQuery.data]);

  return (
    <WSPage>
      <WSQueries
        queries={{
          qCMSWebflowBenefits,
          userProfileQuery,
          qActivities,
          qFeatureFlags
        }}
        renderLoader={() => <WSPage />}
      >
        {({
          userProfileQuery,
          qCMSWebflowBenefits,
          qActivities,
          qFeatureFlagsData
        }) => {
          const enterpriseId = getCurrentEntepriseId(
            store,
            qActivities.data,
            userProfileQuery.data
          );

          const benefits = selectorBenefitsList({
            list: qCMSWebflowBenefits.data,
            userId: userId,
            enterpriseId: enterpriseId,
            categories: [IWebflowBenefitCategory.Insurance],
            isAdditional: true,
            subscription: qSubscription.data,
            featureFlag: qFeatureFlagsData,
            showMasterPolicyEnrollment
          });

          const independentContractorBenefits = benefits.filter(
            benefit => benefit.isAssurely
          );
          const personalBenefits = benefits.filter(
            benefit => !benefit.isAssurely
          );

          const tabs: {
            slug: BenefitsInsuranceTab;
            label: string;
            list: ExtendedWebflowBenefit[];
          }[] = [
            {
              slug: "all",
              label: "All",
              list: benefits
            },
            ...(independentContractorBenefits.length
              ? ([
                  {
                    slug: "independent-contractor",
                    label: "Independent contractor insurance",
                    list: independentContractorBenefits
                  }
                ] as const)
              : []),
            {
              slug: "personal",
              label: "Personal insurance",
              list: personalBenefits
            }
          ];

          return (
            <>
              <WSPageToolbar
                title="Insurance"
                actions={[
                  {
                    label: "Request a new benefit",
                    buttonKind: "Primary",
                    onClick: openRequestBenefitModal
                  }
                ]}
              >
                <WSText.ParagraphSm color="gray600" className={styles.text}>
                  Accessing high quality insurance for independent contractors
                  is difficult. Wingspan has partnered with providers who have
                  designed plans specifically for independent professionals.
                </WSText.ParagraphSm>
              </WSPageToolbar>

              <BenefitsRequestBenefit />

              <Tabs
                tabs={tabs.map(tab => ({
                  label: tab.label,
                  path: `${PATH_BENEFITS_INSURANCE}/${tab.slug}`
                }))}
              />

              <Switch>
                {tabs.map(tab => (
                  <Route
                    key={tab.slug}
                    path={`${PATH_BENEFITS_INSURANCE}/${tab.slug}`}
                    exact
                    render={() => (
                      <WSElement>
                        {tab.list.map(benefit =>
                          benefit.isAssurely ? (
                            <BenefitsInsuranceBlock
                              key={benefit.slug}
                              benefit={benefit}
                              pagePath={getBenefitsInsuranceDetailsPathname(
                                tab.slug,
                                benefit.slug
                              )}
                            />
                          ) : (
                            <BenefitsCMSBlock
                              key={benefit.slug}
                              benefit={benefit}
                              pagePath={getBenefitsInsuranceDetailsPathname(
                                tab.slug,
                                benefit.slug
                              )}
                            />
                          )
                        )}
                      </WSElement>
                    )}
                  />
                ))}

                <Redirect
                  from="*"
                  to={`${PATH_BENEFITS_INSURANCE}/${tabs[0].slug}`}
                />
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
